<template>
  <el-input v-model="password" auto-complete="off" :type="type" :disabled="disabled" @change="updateValue">
    <template slot="append">
      <a
         v-if="strong.score === -1"
        @click="togglePassword"
        :title="$t(type === 'password' ? 'label.show_password' : 'label.hide_password')">
        <i class="far" :class="{ 'fa-eye': type === 'password', 'fa-eye-slash': type !== 'password' }"></i>
      </a>
      <el-tooltip v-else class="item" effect="dark" :content="$t(strongMessage)" placement="top">
        <a :class="[`strength-${strong.score}`]" @click="togglePassword">
          <i class="far" :class="{ 'fa-eye': type === 'password', 'fa-eye-slash': type !== 'password' }"></i>
        </a>
      </el-tooltip>
    </template>
  </el-input>
</template>

<script>
import zxcvbn from 'zxcvbn';
import { isNil } from 'lodash';

export default {
  name: 'nuttrema-password-input',
  props: {
    value: {
      required: true,
      default: null,
    },
    durration: {
      type: Number,
      required: false,
      default: 5000,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    checkStrong: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      type: 'password',
      password: null,
      timer: null,
      strong: {
        score: -1,
      },
    };
  },
  methods: {
    showPassword() {
      this.type = 'text';
      this.startTimer();
    },
    hidePassword() {
      this.type = 'password';
      this.clearTimer();
    },
    togglePassword() {
      if (this.type === 'text') {
        this.hidePassword();
      } else {
        this.showPassword();
      }
    },
    startTimer() {
      this.timer = setTimeout(() => {
        this.hidePassword();
      }, this.durration);
    },
    clearTimer() {
      clearTimeout(this.timer);
    },
    updateValue(inputValue) {
      this.calculateScore(inputValue);
      this.$emit('input', inputValue);
    },
    calculateScore(value) {
      if (this.checkStrong) {
        this.strong = zxcvbn(value);
      } else {
        this.strong.score = -1;
      }
    },
  },
  computed: {
    strongMessage() {
      return `label.password_strong_level_${this.strong.score}`;
    },
  },
  watch: {
    value(newValue) {
      if (!isNil(newValue)) {
        this.calculateScore(newValue);
        this.password = newValue;
      } else {
        this.password = null;
        this.strong.score = -1;
      }
    },
  },
  created() {
    if (!isNil(this.value)) this.calculateScore(this.value);
    this.password = this.value;
  },
};
</script>

<!--

too guessable: risky password
very guessable: protection from throttled online attacks
somewhat guessable: protection from unthrottled online attacks
safely unguessable: moderate protection from offline slow-hash scenario
very unguessable: strong protection from offline slow-hash scenario

-->

<style lang="scss" scoped>

  $password_strong_score_0: #FF5722;
  $password_strong_score_1: #e91e63;
  $password_strong_score_2: #9c27b0;
  $password_strong_score_3: #03a9f4;
  $password_strong_score_4: #8bc34a;

  a {
    > i { color: #97a8be; }
    &:hover, &:focus, &:active {
      > i {
        color: darken(#97a8be, 10%);
      }
    }
  }

  .el-input {
    /deep/ .el-input-group__append {
      .is-error & {
        border-color: #FF5722;
      }
    }

    /deep/ .el-input-group__append {
      width: 4em;
      border: none;
      a {
        display: flex;
        position: absolute;
        border: none;
        border-left: none;
        border-radius: 3px;
        justify-content: center;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        top: 0; left: 0; bottom: 0; right: 0;
        box-shadow: inset 7px 0px 9px -7px rgba(0,0,0,0.35);
        > i {
          align-self: center;
        }
      }
      a.strength-0 {
        background-color: $password_strong_score_0;
        i { color: darken($password_strong_score_0, 20%); }
      }
      a.strength-1 {
        background-color: $password_strong_score_1;
        i { color: darken($password_strong_score_1, 20%); }
      }
      a.strength-2 {
        background-color: $password_strong_score_2;
        i { color: darken($password_strong_score_2, 20%); }
      }
      a.strength-3 {
        background-color: $password_strong_score_3;
        i { color: darken($password_strong_score_3, 20%); }
      }
      a.strength-4 {
        background-color: $password_strong_score_4;
        i { color: darken($password_strong_score_4, 20%); }
      }
    }

  }
</style>



// WEBPACK FOOTER //
// src/components/ui/input/nuttrema-password-input.vue